import { useEffect, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate, useLocation } from '@tanstack/react-router';

import { PlatformScopeSwitch } from './platform-scope-switch';
import { ConsolidatedFilters } from './filters-toolbar';
import { ConsolidatedOverviewPanel } from './overview-panel';
import { ConsolidatedCampaignsPanel } from './campaigns-panel';
import { AudiencePanel } from './audience-panel';
import { ConsolidatedCreativeAdsPanel } from './creative-ads-panel';
import { BudgetPanel } from './budget-panel';
import { ConsolidatedKeywordsTermsPanel } from './keywords-terms-panel';
import { ActivityPanel } from './activity-panel';
import { WidgetGrid, type WidgetConfig } from '@/components/glitch-ui/widget-grid';
import { CONSOLIDATED_TABS, CONSOLIDATED_TAB_IDS, tabAvailability, resolveActiveTab } from '@/lib/performance/tabs';
import { dateWindow } from '@/lib/performance/date-window';
import type { PlatformScope } from '@/lib/performance/types';
import {
  consolidatedPerformanceScopeAtom,
  selectedConsolidatedPerformanceTabAtom,
  consolidatedPerformanceWidgetLayoutAtom,
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceFilterOrgAtom,
  organizationDataAtom,
} from '@/store';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import { useTabPersistence } from '@/hooks/useTabPersistence';
import { tabButtonClass } from '@/components/glitch-ui/tab-card-panel';
import { metaIntegrationStatusQueryOptions, metaTimeSeriesQueryOptions } from '@/lib/query-options';
import type { MetaIntegrationStatus, MetaInsightsWindow } from '@/lib/types';
import { useTestMode } from '@/hooks/useTestMode';
import { cn } from '@/lib/utils';

// Per-tab widget layouts (drag-reorder + resize, persisted per user). The table
// tabs are one full-width widget each. Overview and Activity are omitted — they
// bring their own WidgetGrid and render outside this grid to avoid nested
// react-dnd providers. 'google-overview' is the layout for the Google detail
// grid inside the Overview panel (perf-v2's Overview widgets minus the
// performance-chart, which the top block's cross-platform chart already covers).
const CONSOLIDATED_DEFAULT_LAYOUTS: Record<string, WidgetConfig[]> = {
  'google-overview': [
    { id: 'account-snapshot', width: 100, order: 0 },
    { id: 'problem-alerts', width: 100, order: 1 },
    { id: 'insights', width: 100, order: 2 },
    { id: 'next-steps', width: 100, order: 3 },
    { id: 'overview-metrics', width: 100, order: 4 },
    { id: 'money-context', width: 33, order: 5 },
    { id: 'type-efficiency', width: 66, order: 6 },
    { id: 'conversion-mix-donut', width: 50, order: 7 },
    { id: 'notable-search-terms', width: 50, order: 8 },
    { id: 'ai-actions', width: 50, order: 9 },
    { id: 'top-performers', width: 50, order: 10 },
  ],
  'google-campaigns': [
    { id: 'campaigns-chart', width: 100, order: 0 },
    { id: 'campaigns-table', width: 100, order: 1 },
  ],
  'google-ads': [{ id: 'ads-content', width: 100, order: 0 }],
  'google-keywords': [{ id: 'keywords-content', width: 100, order: 0 }],
  'google-search-terms': [{ id: 'search-terms-content', width: 100, order: 0 }],
  audience: [{ id: 'audience', width: 100, order: 0 }],
  budget: [{ id: 'budget', width: 100, order: 0 }],
};

/**
 * The consolidated Performance shell (#5971, CP-3). Hosts the master platform
 * scope switch, the platform-aware tab bar, and the freshness header; renders the
 * active tab's panel. Per-tab content is wired in CP-6..CP-12 — the panels are
 * placeholders for now. Test-mode gated: non-test-mode users are sent to the
 * existing /performance page.
 */
export function ConsolidatedPerformance() {
  const isTestMode = useTestMode();
  const [scope] = useAtom(consolidatedPerformanceScopeAtom);

  // #6279 — the filter atoms are global but a campaign selection is org-specific:
  // carried across an org switch it matches nothing and silently empties the
  // page. The org marker atom survives this component's withOrgReset remount,
  // so the comparison works even though the switch remounts us.
  const organizationData = useAtomValue(organizationDataAtom);
  const [filterOrg, setFilterOrg] = useAtom(consolidatedPerformanceFilterOrgAtom);
  const setSelectedCampaignIds = useSetAtom(consolidatedPerformanceCampaignIdsAtom);
  const setCampaignTypes = useSetAtom(consolidatedPerformanceCampaignTypesAtom);
  useEffect(() => {
    const orgId = organizationData?.id;
    if (orgId == null || filterOrg === orgId) return;
    if (filterOrg !== null) {
      setSelectedCampaignIds(new Set());
      setCampaignTypes(new Set(['all']));
    }
    setFilterOrg(orgId);
  }, [organizationData?.id, filterOrg, setSelectedCampaignIds, setCampaignTypes, setFilterOrg]);

  // #5996 — reflect the active tab in the URL hash (#campaigns), matching the
  // business page. Hash wins on refresh / back-forward; the atom persists it
  // across sidebar navigation. (Scope stays an atom — clean, #tab-only URLs.)
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTab, handleTabChange } = useTabPersistence(
    selectedConsolidatedPerformanceTabAtom,
    CONSOLIDATED_TAB_IDS,
    'overview',
    (tabId, replace) => navigate({ to: '/performance-consolidated', hash: tabId, replace }),
    location.hash,
  );

  const availability = useMemo(
    () => new Map(CONSOLIDATED_TABS.map((t) => [t.id, tabAvailability(t, scope)] as const)),
    [scope],
  );
  const { getTabLayout, handleReorder, handleResize } = useWidgetLayout(
    consolidatedPerformanceWidgetLayoutAtom,
    CONSOLIDATED_DEFAULT_LAYOUTS,
  );

  if (!isTestMode) return <Navigate to="/performance" />;

  const effectiveTab = resolveActiveTab(activeTab, scope);

  return (
    <div className="pt-1.5">
      {/* Sticky folder-tab row — pinned to the top of the page scroller like the
          other tabbed pages (TabCardPanel). Flowing content below lets the tabs
          stick instead of the whole surface scrolling (#5971). */}
      <div className="sticky top-0 z-20 bg-[#fafafb]">
        <div className="flex items-end gap-1">
          {CONSOLIDATED_TABS.map((tab) => {
            const avail = availability.get(tab.id)!;
            const isActive = tab.id === effectiveTab;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                disabled={!avail.available}
                title={avail.available ? undefined : avail.reason}
                onClick={() => avail.available && handleTabChange(tab.id)}
                className={cn(
                  tabButtonClass(isActive),
                  'whitespace-nowrap',
                  !avail.available && 'cursor-not-allowed opacity-40 hover:bg-gray-100 hover:text-gray-700',
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* White content card the active tab connects into (#5971): filter toolbar
          at the top, panels below. The active folder tab's transparent bottom
          border merges into this card. */}
      <div className="-mt-px rounded-b-lg rounded-tr-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 pb-2 pt-3">
          <div className="flex flex-wrap items-start justify-end gap-3">
            <div className="flex flex-col gap-1.5">
              <PlatformScopeSwitch />
              <FreshnessNote scope={scope} />
            </div>
            <ConsolidatedFilters scope={scope} activeTab={effectiveTab} />
          </div>
        </div>
        <div className="p-4">
          {/* MERGE-REVIEW: dev had a MetaComingSoon placeholder for scope === 'meta' (its 1.13
              port lacked Meta wiring); release has real Meta panels, so TabPanel renders for all
              scopes here. */}
          <TabPanel
            tabId={effectiveTab}
            scope={scope}
            getTabLayout={getTabLayout}
            onReorder={handleReorder}
            onResize={handleResize}
          />
        </div>
      </div>
    </div>
  );
}

function FreshnessNote({ scope }: { scope: PlatformScope }) {
  const metaInScope = scope !== 'google';
  const { data: metaStatus } = useQuery({
    ...metaIntegrationStatusQueryOptions(),
    enabled: metaInScope,
  });

  // When the integration reports no last_sync_at we can still timestamp the Meta
  // figures from the insights window's as-of date. Only fetch it when needed (no
  // sync time); on the Overview tab this dedupes with the chart's own query.
  const days = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const window = useMemo(() => dateWindow(days), [days]);
  const { data: metaSeries } = useQuery(
    metaTimeSeriesQueryOptions(
      window.start_date,
      window.end_date,
      // Only worth fetching for a CONNECTED integration missing last_sync_at —
      // "not connected" needs no as-of date (#6278).
      metaInScope && !!metaStatus?.is_connected && !metaStatus?.last_sync_at,
    ),
  );

  const parts: string[] = [];
  if (scope !== 'meta') parts.push('Google: live');
  if (metaInScope) parts.push(`Meta: ${metaFreshnessLabel(metaStatus, metaSeries?.window ?? null)}`);
  return <span className="whitespace-nowrap text-xs text-gray-500">{parts.join(' · ')}</span>;
}

// Google reads live; Meta is snapshot-backed on a ~4-hour sync. Prefer the
// integration's last_sync_at ("synced 3h ago"); when that's missing, timestamp
// the figures with the insights window's as-of date instead of a vague label.
function metaFreshnessLabel(status: MetaIntegrationStatus | undefined, window: MetaInsightsWindow | null): string {
  if (status && !status.is_connected) return 'not connected';
  if (status?.last_sync_at) return `synced ${relativeTime(status.last_sync_at)}`;
  if (window?.date_stop) return `as of ${formatAsOf(window.date_stop)}`;
  return 'snapshot-backed';
}

function formatAsOf(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'recently';
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function TabPanel({
  tabId,
  scope,
  getTabLayout,
  onReorder,
  onResize,
}: {
  tabId: string;
  scope: PlatformScope;
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  // Overview and Activity bring their own WidgetGrid (react-dnd), so they render
  // outside this grid — only one dnd provider is ever mounted at a time.
  if (tabId === 'overview') {
    return (
      <ConsolidatedOverviewPanel
        scope={scope}
        getTabLayout={getTabLayout}
        onReorder={onReorder}
        onResize={onResize}
      />
    );
  }
  if (tabId === 'activity') return <ActivityPanel />;
  // Campaigns is hybrid by scope (#5995): Google reuses perf-v2's CampaignsTab
  // (its own WidgetGrid), Meta / All keep the merged CampaignsPanel table.
  if (tabId === 'campaigns') {
    return (
      <ConsolidatedCampaignsPanel
        scope={scope}
        getTabLayout={getTabLayout}
        onReorder={onReorder}
        onResize={onResize}
      />
    );
  }
  // Creative & Ads is hybrid by scope (#5995): Google reuses perf-v2's AdsTab
  // (its own WidgetGrid), Meta / All keep the merged CreativeAdsPanel table.
  if (tabId === 'creative-ads') {
    return (
      <ConsolidatedCreativeAdsPanel
        scope={scope}
        getTabLayout={getTabLayout}
        onReorder={onReorder}
        onResize={onResize}
      />
    );
  }
  // Keywords & Terms is Google-only (the shell gates it off under Meta), so no
  // scope branch — it reuses perf-v2's KeywordsTab + SearchTermsTab (its own
  // WidgetGrid) fed by the shared usePerformanceData hook (#5995).
  if (tabId === 'keywords-terms') {
    return (
      <ConsolidatedKeywordsTermsPanel
        getTabLayout={getTabLayout}
        onReorder={onReorder}
        onResize={onResize}
      />
    );
  }

  return (
    <WidgetGrid
      layout={getTabLayout(tabId)}
      onReorder={(layout) => onReorder(tabId, layout)}
      onResize={(id, width) => onResize(tabId, id, width)}
    >
      {(widgetId) => renderConsolidatedWidget(widgetId, scope)}
    </WidgetGrid>
  );
}

function renderConsolidatedWidget(widgetId: string, scope: PlatformScope) {
  switch (widgetId) {
    case 'audience':
      return <AudiencePanel scope={scope} />;
    case 'budget':
      return <BudgetPanel scope={scope} />;
    default:
      return null;
  }
}
